import "webrtc-adapter";
import RecordRTC from "recordrtc";
import { isSafari, isEdge } from "./utils/detect-browser";
import { addFileInfo } from "./utils/file-util";
import setSrcObject from "./utils/browser-shim";
import Converter from "./Converter";

function applyAudioWorkaround() {
  if (isSafari() || isEdge()) {
    if (isSafari() && window.MediaRecorder !== undefined) {
      // this version of Safari has MediaRecorder
      return {};
    }

    // support recording in safari 11/12
    console.log("applied audio workarounds for this browser");
    return {
      recorderType: RecordRTC.StereoAudioRecorder,
      sampleRate: 44100,
      bufferSize: 4096,
      numberOfAudioChannels: 2
    };
  }

  return {};
}

const defaultOptions = {
  type: "audio",
  bufferSize: 4096,
  sampleRate: 44100,
  bitRate: 128,
  numberOfAudioChannels: 2,
  disableLogs: false
  // mimeType: "audio/mp3"
};

class Recorder {
  constructor(options = {}, debug = true) {
    this.status = null;
    this.recordedData = null;
    this.convertedData = null;

    this.debug = debug;

    this.options = {
      ...defaultOptions,
      ...options,
      ...applyAudioWorkaround(),

      disableLogs: !this.debug
    };
  }

  async setStatus() {
    this.status = await this.engine.getState();
  }

  async createRecorder() {
    if (!this.engine) {
      if (
        typeof navigator.mediaDevices === "undefined" ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error(
          "This browser does not support navigator.mediaDevices.getUserMedia"
        );
      }

      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.engine = new RecordRTC.RecordRTCPromisesHandler(
        this.stream,
        this.options
      );
    }

    this.recordedData = null;
    this.convertedData = null;
    await this.reset();

    return this;
  }

  async onStopRecording(audioVideoURL) {
    URL.revokeObjectURL(audioVideoURL);

    this.recordedData = await this.engine.getBlob();

    if (this.recordedData.audio !== undefined) {
      this.recordedData = this.recordedData.audio;
    }

    addFileInfo(this.recordedData);
    if (this.recordedData.type == "audio/webm") {
      this.convertedData = await Converter.convert(this.recordedData);
    }

    return this.setStatus();
  }

  stop() {
    return this.engine.stopRecording().then(this.onStopRecording.bind(this));
  }

  start() {
    return this.engine.startRecording().then(this.setStatus.bind(this));
  }

  pause() {
    return this.engine.pauseRecording().then(this.setStatus.bind(this));
  }

  reset() {
    return this.engine.reset().then(this.setStatus.bind(this));
  }

  resume() {
    return this.engine.resumeRecording().then(this.setStatus.bind(this));
  }

  dispose() {
    if (this.recordedData !== undefined) {
      URL.revokeObjectURL(this.recordedData);
    }

    this.stopStream();

    return this.destroy();
  }

  async destroy() {
    if (this.engine) {
      await this.engine.destroy();
      this.status = null;
      this.engine = null;
    }

    return !this.engine;
  }

  stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
      });
      this.stream = null;
    }
  }

  load(url, mediaElement) {
    if (url instanceof Blob || url instanceof File) {
      mediaElement.srcObject = null;
      mediaElement.src = URL.createObjectURL(url);
    } else {
      setSrcObject(url, mediaElement);
    }
  }

  static getFileObject(blob, fileName, params = {}) {
    return new File([blob], fileName || blob.name, {
      type: blob.type,
      ...params
    });
  }
}

export default Recorder;
